import { useEffect } from "react";
import { Megaphone } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { embedUrl } from "@/lib/media";
import { blockStyle, blockTestid, layoutOf } from "@/lib/blocks";
import { loadFont } from "@/lib/design";
import { Slideshow } from "@/components/Slideshow";
import { VideoGallery } from "@/components/VideoGallery";
import { PhotoGallery } from "@/components/PhotoGallery";
import { PhotoAlbums } from "@/components/PhotoAlbums";
import { useVideoUrl } from "@/lib/useVideoUrl";
import { useLang } from "@/context/LangContext";
import { useSite } from "@/context/SiteContext";

const wrap = "mx-auto max-w-[1400px] px-5 py-6 md:px-10";

// Shows a placeholder only inside the Visual Editor so empty blocks can still be clicked.
const Txt = ({ as: Tag = "p", value, rec, edit, hint, className, testid, style }) => {
  if (!value && !edit) return null;
  return <Tag data-ve-blk={rec} data-testid={testid} style={style} className={`${className} ${value ? "" : "italic opacity-60"}`}>{value || hint}</Tag>;
};

const Video = ({ url: raw }) => {
  const url = useVideoUrl(raw);
  if (!url) return <div className="aspect-video w-full rounded-[28px] bg-black" />;
  const embed = embedUrl(url);
  if (embed) return <iframe src={embed} title="Video" allow="encrypted-media; picture-in-picture; fullscreen" allowFullScreen className="aspect-video w-full rounded-[28px] bg-black" />;
  return <video src={fileUrl(url)} controls playsInline preload="metadata" className="aspect-video w-full rounded-[28px] bg-black object-contain" />;
};

const Media = ({ b, rec, edit }) => {
  const { pick } = useLang();
  const caption = pick(b.en, b.mh);
  if (!b.url && !edit) return null;
  return (
    <figure>
      {!b.url && <div data-ve-blk={`${rec}url`} className="grid aspect-video w-full place-items-center rounded-[28px] border-2 border-dashed border-[#D4AF37]/50 font-ui text-sm text-[#B7BFCC]">Click to add a {b.type}</div>}
      {b.url && b.type === "photo" && <img data-ve-blk={`${rec}url`} src={fileUrl(b.url)} alt={caption || ""} loading="lazy" className="max-h-[720px] w-full rounded-[28px] object-cover" />}
      {b.url && b.type === "video" && <div data-ve-blk={`${rec}url`} className={edit ? "pointer-events-none" : ""}><Video url={b.url} /></div>}
      <Txt as="figcaption" value={caption} rec={rec} edit={edit} hint="Click to add a caption" className="mt-3 font-ui text-sm text-[#B7BFCC]" />
    </figure>
  );
};

export const CustomBlock = ({ page, b }) => {
  const { pick } = useLang();
  const { veMode } = useSite();
  const edit = veMode === "edit";
  const rec = `${page}|${b.id}|`;
  const body = pick(b.en, b.mh);
  const style = blockStyle(b);
  useEffect(() => { if (b.font) loadFont(b.font); }, [b.font]);
  let inner = null;
  if (b.type === "heading") inner = <Txt as="h2" style={style} testid={`block-text-${b.id}`} value={body} rec={rec} edit={edit} hint="Click to write a heading" className="font-display text-4xl font-semibold leading-tight text-[#F4EEE2] md:text-5xl" />;
  if (b.type === "text") inner = <Txt style={style} testid={`block-text-${b.id}`} value={body} rec={rec} edit={edit} hint="Click to write text" className={`whitespace-pre-line text-base leading-relaxed text-[#C9D0DB] md:text-lg ${b.align || b.bg ? "" : "max-w-3xl"}`} />;
  if (b.type === "announcement") {
    inner = (
      <div className="flex gap-5 rounded-[28px] border border-[#D4AF37]/30 bg-[#131C2E] p-6 md:p-9">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#D4AF37]/15 text-[#D4AF37]"><Megaphone className="h-5 w-5" /></span>
        <div className="min-w-0">
          <Txt as="h3" value={pick(b.title_en, b.title_mh)} rec={`${rec}title_`} edit={edit} hint="Click to write the announcement title" className="font-display text-3xl font-semibold text-[#F4EEE2]" />
          <Txt value={body} rec={rec} edit={edit} hint="Click to write the announcement" className="mt-2 whitespace-pre-line text-base leading-relaxed text-[#C9D0DB]" />
        </div>
      </div>
    );
  }
  if (b.type === "photo" || b.type === "video") inner = <Media b={b} rec={rec} edit={edit} />;
  if (b.type === "slideshow") {
    const urls = Array.isArray(b.urls) ? b.urls : [];
    inner = urls.length ? <Slideshow id={b.id} urls={urls} caption={body} /> : edit ? <div className="grid aspect-[16/8] w-full place-items-center rounded-[28px] border-2 border-dashed border-[#D4AF37]/50 font-ui text-sm text-[#B7BFCC]">Add photos to this slideshow in the "This page" tab</div> : null;
  }
  if (b.type === "videos") {
    const has = Array.isArray(b.items) && b.items.length > 0;
    inner = has ? <VideoGallery id={b.id} items={b.items} cols={b.cols} /> : edit ? <div className="grid aspect-[16/5] w-full place-items-center rounded-[28px] border-2 border-dashed border-[#D4AF37]/50 font-ui text-sm text-[#B7BFCC]">Paste video links into this gallery in the "This page" tab</div> : null;
  }
  if (b.type === "photos") {
    const has = Array.isArray(b.items) && b.items.length > 0;
    inner = has ? <PhotoGallery id={b.id} items={b.items} cols={b.cols} /> : edit ? <div className="grid aspect-[16/5] w-full place-items-center rounded-[28px] border-2 border-dashed border-[#D4AF37]/50 font-ui text-sm text-[#B7BFCC]">Upload photos to this gallery in the "This page" tab</div> : null;
  }
  if (b.type === "albums") {
    const has = Array.isArray(b.albums) && b.albums.some((a) => a.photos?.length);
    inner = has ? <PhotoAlbums id={b.id} albums={b.albums} cols={b.cols} /> : edit ? <div className="grid aspect-[16/5] w-full place-items-center rounded-[28px] border-2 border-dashed border-[#D4AF37]/50 font-ui text-sm text-[#B7BFCC]">Create albums and add photos in the "This page" tab</div> : null;
  }
  if (b.type === "divider") inner = <hr data-testid={`divider-${b.id}`} aria-hidden="true" style={{ border: 0, borderTop: `${b.thick || 2}px ${b.line || "solid"} ${b.color || "#C8862F"}` }} />;
  if (!inner) return null;
  return <section data-testid={`block-${b.id}`} className={wrap}>{inner}</section>;
};

// Renders a page as its saved list of built-in sections and added blocks.
export const PageLayout = ({ page, sections }) => {
  const { settings } = useSite();
  return layoutOf(page, settings?.layouts?.[page]).map((b) => {
    if (b.hidden) return null;
    if (b.type !== "section") return <CustomBlock key={b.id} page={page} b={b} />;
    const node = sections[b.key];
    return node ? <div key={b.id} data-testid={blockTestid(page, b)}>{node}</div> : null;
  });
};
